import React, { useEffect, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";

import { Config } from "@eagerdog/config";
import { IAddress, IClub } from "@eagerdog/interfaces";

import { apiService } from "src/services";

import { useDebounce } from "src/hooks/useDebounce";
import { Modal } from "src/components/Modal/Modal";
import LoadingSpinner from "src/components/LoadingSpinner";
import { toast } from "src/components/Toast/ToastManager";
import { DonationForm } from "../DonationForm";

import styles from "./DonationModal.module.scss";

interface IProps {
  club: IClub
  toggle: () => void
}

const DonationModal: React.FC<IProps> = ({ club, toggle }) => {
  const presetAmounts = club.donation_amounts_preset;

  const [loading, setLoading] = useState(false);
  const [updatingIntent, setUpdatingIntent] = useState(false);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null>>();
  const [donationIntent, setDonationIntent] = useState<any>(undefined);
  const [intentError, setIntentError] = useState<any>(undefined);

  const [donationAmount, setDonationAmount] = useState(presetAmounts?.[0] ?? 0);
  const [customDonationAmount, setCustomDonationAmount] = useState<undefined | number>();
  const debouncedCustomAmount = useDebounce(customDonationAmount, 800);

  const getCurrency = () => {
    if (!club) {
      return "CAD";
    }

    return ["CA", "Canada"].includes(club.address.country ?? "") ? "CAD" : "USD";
  }

  useEffect(() => {
    if (club) {
      setLoading(true);
      apiService.createClubDonationIntent(
        club._id,
        {
          currency: getCurrency(),
          amount_cents: donationAmount,
        }
      )
        .then((response) => {
          setDonationIntent(response);
          setStripePromise(
            loadStripe(
              Config.web.REACT_APP_STRIPE_PUBLIC_KEY || "",
              { stripeAccount: response.account_number }
            )
          )
        })
        .catch((error) => {
          setIntentError(error);
          toast.show({
            title: "Donation",
            errorDetails: error,
            content: "Something went wrong, please try again.",
            duration: 10000,
            type: "fail"
          });
        })
        .finally(() => {
          setLoading(false);
        })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: getCurrency(),
      minimumFractionDigits: 2,
    }).format(amount / 100);
  };

  const updateIntent = (amount: number) => {
    setUpdatingIntent(true);
    apiService.updateClubDonationIntent(
      club._id,
      donationIntent.payment_intent_id,
      {
        currency: getCurrency(),
        amount_cents: amount,
      }
    )
      .then(() => {
        setDonationAmount(amount);
      })
      .catch((error) => {
        toast.show({
          title: "Donation",
          errorDetails: error,
          content: "Something went wrong, please try again.",
          duration: 10000,
          type: "fail"
        });
      })
      .finally(() => {
        setUpdatingIntent(false);
      });
  }

  const handleCustomAmountChange = (event: any) => {
    setCustomDonationAmount(Number(event.target.value));
  }

  useEffect(() => {
    if (debouncedCustomAmount && debouncedCustomAmount >= 10) {
      updateIntent(debouncedCustomAmount * 100);
    } else if (debouncedCustomAmount && debouncedCustomAmount < 10) {
      toast.show({
        title: "Donation",
        content: "Minimum donation amount amount must be 10$.",
        duration: 5000,
        type: "fail"
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCustomAmount]);

  const handleAmountChange = (amount: number) => {
    if (amount === donationAmount || !club) {
      return;
    }

    setCustomDonationAmount(undefined);
    updateIntent(amount);
  }

  const handleDonationSuccess = (
    paymentIntentId: string,
    donorName: string,
    donorEmail: string,
    donorAddress: IAddress,
    donorPhone: string
  ) => {
    return apiService.createClubDonation(
      club._id,
      {
        payment_intent_id: paymentIntentId,
        amount_cents: customDonationAmount ? customDonationAmount * 100 : donationAmount,
        currency: getCurrency(),
        donor_name: donorName,
        donor_email: donorEmail,
        donor_address: donorAddress,
        donor_phone: donorPhone,
        payment_method_type: "card",
      }
    )
  }

  const handleDonationError = (error: any) => {
    toast.show({
      title: "Donation",
      errorDetails: error,
      content: "Could not process donation transaction, please try again.",
      duration: 10000,
      type: "fail"
    });
  }

  return (
    <Modal
      className="clubDonationModal"
      isShown
      hide={toggle}
      modalContent={
        <div className={styles.DonationModal}>
          <div className={styles.title}>
            <span>Donation Form</span>
            <span>You are donating to {club.club_name}. Donations made through the Eager Dog platform go directly to the club and are not tax deductible.</span>
          </div>
          {loading && <LoadingSpinner/>}
          {!loading && !!intentError && <div className={styles.error}>Could not prepare payment form. Please try again</div>}
          {!loading && !!stripePromise && !!donationIntent &&
            <Elements stripe={stripePromise} options={{ clientSecret: donationIntent.client_secret }}>
              <div className={styles.DonationModalContent}>
                <DonationForm
                  paymentIntentId={donationIntent.payment_intent_id}
                  disableSubmit={(!!customDonationAmount && customDonationAmount < 10) || !donationAmount}
                  handleModalClose={toggle}
                  onSubmit={handleDonationSuccess}
                  onError={handleDonationError}
                  presetAmounts={presetAmounts}
                  donationAmount={donationAmount}
                  customDonationAmount={customDonationAmount}
                  updatingIntent={updatingIntent}
                  formatCurrency={formatCurrency}
                  handleAmountChange={handleAmountChange}
                  handleCustomAmountChange={handleCustomAmountChange}
                />
              </div>
            </Elements>
          }
        </div>
      }
    />
  )
}

export { DonationModal }
